// import components
import { FormContent } from 'components/admin-portal/Settings/Match/components/FormContent';
import { AdminSettingsFormWrapper } from 'components/shared/AdminSettings/AdminSettingsFormWrapper';
import { showErrorSnackbar } from 'components/shared/Snackbar';
import {
  DTR_SETTINGS_GROUP,
  MATCH_SETTINGS_GROUP,
  TRAVELCOST_SETTINGS_GROUP
} from '../constants';
import PlanningCycle from './PlanningCycle';

// import hooks
import { useEffect, useState } from 'react';
import useGetRosterStateSettings from '../hooks/useGetRosterStateSettings';
import useSaveRosterStateSettings from '../hooks/useSaveRosterStateSettings';

// import queries & mutations
import { useGetCostCenterSettings } from 'queries';
import { useSaveResource } from 'RestAPIs/useSaveResource';

// import types
import { AdminSettingsResponse } from 'components/types';
import { AxiosError } from 'axios';

// import utils
import { COSTCENTER_SETTINGS_GROUP } from 'RestAPIs/endpoints';
import { t } from 'i18next';
import * as yup from 'yup';
import {
  transformAdminSettingsToFormValues,
  transformAdminSettingsToRequestValues
} from 'components/utils';

const CostCenterSettings = ({
  costCenterId,
  settingsGroup,
  fullWidth = false
}) => {
  const { data: rosterStateSettings, refetch: refetchRosterStateSettings } =
    useGetRosterStateSettings(costCenterId);
  const { mutate: saveRosterSettings } = useSaveRosterStateSettings();

  const isRosterStateSettings = settingsGroup === 'roster-state-settings';

  const { data: settings = [], isFetching } =
    useGetCostCenterSettings<AdminSettingsResponse>(
      settingsGroup,
      costCenterId,
      { enabled: !isRosterStateSettings }
    );

  const { mutate: saveSetting } = useSaveResource({
    url: COSTCENTER_SETTINGS_GROUP,
    id: settings[0]?.groupId
  });

  // Initialize state for the checkboxes
  const [isPublishedEnabled, setIsPublishedEnabled] = useState(false);
  const [isVisibleEnabled, setIsVisibleEnabled] = useState(false);

  const [openPlanningCycleDialog, setOpenPlanningCycleDialog] = useState({
    published: false,
    visible: false
  });

  // Set the state once the data is fetched from the API
  useEffect(() => {
    if (rosterStateSettings) {
      const publishedSetting = rosterStateSettings.find(
        (setting) => setting.rosterState === 'PUBLISHED'
      );
      const visibleSetting = rosterStateSettings.find(
        (setting) => setting.rosterState === 'VISIBLE'
      );

      setIsPublishedEnabled(publishedSetting?.enabled || false);
      setIsVisibleEnabled(visibleSetting?.enabled || false);
    }
  }, [rosterStateSettings]);

  // Handle opening the dialogs for planning cycles
  const handleDialogOpen = (type: 'PUBLISHED' | 'VISIBLE') => {
    setOpenPlanningCycleDialog((prevState) => ({
      ...prevState,
      [type.toLowerCase()]: true
    }));
  };

  const handleDialogClose = (
    type: 'PUBLISHED' | 'VISIBLE',
    wasSaved: boolean
  ) => {
    setOpenPlanningCycleDialog((prevState) => ({
      ...prevState,
      [type.toLowerCase()]: false
    }));

    if (wasSaved) {
      if (type === 'PUBLISHED') setIsPublishedEnabled(true);
      if (type === 'VISIBLE') setIsVisibleEnabled(true);

      refetchRosterStateSettings();
    }
  };

  // Save roster state when checkbox is unchecked
  const handleCheckboxChange = (
    type: 'PUBLISHED' | 'VISIBLE',
    checked: boolean
  ) => {
    if (!checked) {
      const rosterStatePayload = {
        rosterState: type,
        enabled: false,
        recurrenceRule: null,
        cycleWindow: null,
        rounds: []
      };
      saveRosterSettings(
        { costCenterId, planningCyclePayload: rosterStatePayload },
        {
          onSuccess: () => {
            if (type === 'PUBLISHED') setIsPublishedEnabled(false);
            if (type === 'VISIBLE') setIsVisibleEnabled(false);
          }
        }
      );
    } else {
      handleDialogOpen(type);
    }
  };

  if (isRosterStateSettings) {
    const publishedCycle = rosterStateSettings?.find(
      (setting) => setting.rosterState === 'PUBLISHED'
    );
    const visibleCycle = rosterStateSettings?.find(
      (setting) => setting.rosterState === 'VISIBLE'
    );

    return (
      <PlanningCycle
        isPublishedEnabled={isPublishedEnabled}
        setIsPublishedEnabled={setIsPublishedEnabled}
        isVisibleEnabled={isVisibleEnabled}
        setIsVisibleEnabled={setIsVisibleEnabled}
        handleDialogOpen={handleDialogOpen}
        handleCheckboxChange={handleCheckboxChange}
        handleDialogClose={handleDialogClose}
        openPlanningCycleDialog={openPlanningCycleDialog}
        costCenterId={costCenterId}
        publishedCycle={publishedCycle}
        visibleCycle={visibleCycle}
      />
    );
  }

  const getSchema = (group: string) => {
    if (group === MATCH_SETTINGS_GROUP) {
      return yup.object({
        self_match_flex_match_connect: yup.boolean(),
        self_match: yup.string(),
        self_match_roster_match_connect: yup.boolean(),
        self_match_flex_deployableasflex_action: yup.string(),
        self_match_automatic_publish_preference2_action: yup.string(),
        self_match_automatic_visible_preference1_action: yup.string(),
        self_match_automatic_publish_own_action: yup.string(),
        self_match_automatic_publish_shared_action: yup.string(),
        self_match_automatic_visible_own_action: yup.string(),
        self_match_automatic_visible_shared_action: yup.string(),
        self_match_automatic_publish_preference1_action: yup.string(),
        self_match_automatic_visible_preference2_action: yup.string(),
        self_match_ignore_negative_balance: yup.boolean(),
        self_match_flex_preference1_action: yup.string(),
        self_match_flex_deployableasflexpreference1_action: yup.string(),
        self_match_flex_flex_action: yup.string()
      });
    }
    if (group === DTR_SETTINGS_GROUP) {
      return yup.object({
        dtr_calculation_enabled: yup.boolean()
      });
    }
    if (group === TRAVELCOST_SETTINGS_GROUP) {
      return yup.object({
        'exclude-travelcost-calculation': yup.boolean()
      });
    }
    return yup.object().shape({}); // default empty schema
  };

  const onSubmit = async (formData) => {
    // 1. Handle "self-match"
    if (settingsGroup === MATCH_SETTINGS_GROUP) {
      const matchSettings = settings[0]?.settings;

      saveSetting(
        {
          ...settings[0],
          settings: transformAdminSettingsToRequestValues(
            formData,
            matchSettings
          ),
          active: settings[0].active
        },
        {
          onError: (error: AxiosError<any>) => {
            if (
              error?.response?.data?.error?.message ===
              'user_with_no_approve_hours_grant'
            ) {
              showErrorSnackbar(
                t('costCenterSettings.error.userWithNoApproveHoursGrant')
              );
            }
          }
        }
      );
    }

    // 2. Handle "exclude-travelcost-calculation"
    if (settingsGroup === TRAVELCOST_SETTINGS_GROUP) {
      const excludeTravelCostSettings = settings[0]?.settings.filter(
        (setting) => setting.key === 'exclude-travelcost-calculation'
      );

      const newExcludeTravelCostSetting = {
        key: 'exclude-travelcost-calculation',
        value: formData['exclude-travelcost-calculation'] // Get the value from the form data
      };

      const transformedExcludeTravelCostSettings =
        excludeTravelCostSettings?.length
          ? transformAdminSettingsToRequestValues(
              formData,
              excludeTravelCostSettings
            )
          : [newExcludeTravelCostSetting]; // Create new if none exist

      saveSetting(
        {
          ...settings[0],
          settings: transformedExcludeTravelCostSettings,
          groupName: settingsGroup,
          active: settings[0]?.active || true
        },
        {
          onError: (error: AxiosError<any>) => {
            if (
              error?.response?.data?.error?.message ===
              'user_with_no_approve_hours_grant'
            ) {
              showErrorSnackbar(
                t('costCenterSettings.error.userWithNoApproveHoursGrant')
              );
            }
          }
        }
      );
    }

    // 3. Handle "dtr.calculation.enabled"
    if (settingsGroup === DTR_SETTINGS_GROUP) {
      const dtrCalculationSettings = settings[0]?.settings.filter(
        (setting) => setting.key === 'dtr.calculation.enabled'
      );

      const newDtrCalculationSetting = {
        key: 'dtr.calculation.enabled',
        value: formData['dtr_calculation_enabled']
      };

      const transformedDtrCalculationSettings = dtrCalculationSettings?.length
        ? transformAdminSettingsToRequestValues(
            formData,
            dtrCalculationSettings
          )
        : [newDtrCalculationSetting];

      saveSetting({
        ...settings[0],
        settings: transformedDtrCalculationSettings,
        groupName: settingsGroup,
        active: settings[0]?.active || true
      });
    }
  };

  return (
    <>
      <AdminSettingsFormWrapper
        submitForm={onSubmit}
        schema={getSchema(settingsGroup)}
        fixedHeightForm={false}
        isLoading={false}
        data={transformAdminSettingsToFormValues(settings[0])}
        isFetching={isFetching}
        fullWidth={fullWidth}
      >
        <FormContent settingsGroup={settingsGroup} />
      </AdminSettingsFormWrapper>
    </>
  );
};

export default CostCenterSettings;
